import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  CircleDollarSign,
  HandCoins,
  Percent,
  RefreshCw,
  Wallet,
} from "lucide-react";
import { Button, Card, DateRangeField, MultiSelectField, PageLoader, SearchBar } from "../components/ui";
import FiltersSheet from "../components/FiltersSheet";
import OrdemAgrupamento from "../components/OrdemAgrupamento";
import PreFiltroField from "../components/PreFiltroField";
import usePageFilters from "../hooks/usePageFilters";
import DashboardTabs from "../components/DashboardTabs";
import { cn } from "../lib/utils";
import { getUsuarioLogado } from "../services/LoginService";
import { listarBancosAtivos, listarConveniosAtivos } from "../services/joinService";
import { listarParceiros } from "../services/parceiroService";
import { buscarComissaoRepasse } from "../services/relatorioService";
import { listarTodosUsuariosAtivos } from "../services/usuarioService";
import { getCurrentBusinessMonthRange, getTodayIso } from "../utils/dateFilters";
import { kpiGradientStyle } from "../utils/kpiCard";

// Comissões: a tabela dinâmica que o comercial usava no Excel (Banco › Convênio › Parceiro), a partir
// do endpoint api/ProducaoBancoConvenioVendedor da Join. Comissões pelos percentuais da capa do
// contrato — valor previsto, não o pago. Página própria (não aba de Vendas) por decisão do dono em
// 2026-09-15; todo usuário acessa, limitado à própria equipe como no dashboard.

const STORAGE_KEY = "relatorio-comissao-repasse:filtros:v1";
const TYPE_ORDER = { NOVO: 0, PORT: 1, REFIN: 2 };
const TIPO_PERIODO_OPTIONS = [
  { value: "APROVACAO", label: "Aprovação", icon: CheckCircle2 },
  { value: "CADASTRO", label: "Cadastro", icon: BriefcaseBusiness },
];
const REPASSE_COLOR = "var(--chart-4)";
const VALUE_COLUMNS = [
  { key: "producaoLiquida", label: "Produção líquida" },
  { key: "comissaoCorrespondente", label: "Com. correspondente" },
  { key: "comissaoParceiro", label: "Com. parceiro" },
  { key: "repasse", label: "Repasse parceiro" },
];

function isAdminUser(user) {
  return String(user?.cargo || "").trim().toLowerCase() === "admin";
}

function getDefaultFilters() {
  const businessMonth = getCurrentBusinessMonthRange();
  return {
    dataInicio: businessMonth.from,
    dataFim: businessMonth.to,
    bancosIds: [],
    conveniosIds: [],
    tiposConvenio: [],
    supervisorIds: [],
    tipoPeriodo: "APROVACAO",
  };
}

function loadFilters() {
  const fallback = getDefaultFilters();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const toStrings = (value) => (Array.isArray(value) ? value.map(String) : []);
    return {
      dataInicio: parsed?.dataInicio || fallback.dataInicio,
      dataFim: parsed?.dataFim || fallback.dataFim,
      bancosIds: toStrings(parsed?.bancosIds),
      conveniosIds: toStrings(parsed?.conveniosIds),
      tiposConvenio: toStrings(parsed?.tiposConvenio),
      supervisorIds: toStrings(parsed?.supervisorIds),
      tipoPeriodo: TIPO_PERIODO_OPTIONS.some((option) => option.value === parsed?.tipoPeriodo)
        ? parsed.tipoPeriodo
        : fallback.tipoPeriodo,
    };
  } catch {
    return fallback;
  }
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

// Busca sem acento e sem diferença de maiúsculas: "cartao" encontra "CARTÃO".
function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLocaleLowerCase("pt-BR")
    .trim();
}

function formatCurrency(value, fractionDigits = 2) {
  return toNumber(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatPercent(value) {
  if (value === null) return "—";
  return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function formatIsoToBr(value) {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${day}/${month}/${year}` : "";
}

// Repasse de um nível = soma da comissão do parceiro ÷ soma da comissão do correspondente. Não é a
// média dos repasses das linhas: é assim que a tabela dinâmica chega nos 80,31% do total geral.
function computeRepasse(node) {
  return node.comissaoCorrespondente > 0 ? (node.comissaoParceiro / node.comissaoCorrespondente) * 100 : null;
}

// Chave do grupo no mapa `repasses` da resposta: as dimensões do nó, sempre na ordem banco, convênio,
// parceiro, seja qual for a ordem de agrupamento na tela. Tem que bater com
// ComissaoRepasseCalculadora no backend; "" é o total geral.
function repasseKey(dims) {
  return ["b", "c", "p"].filter((prefixo) => prefixo in dims).map((prefixo) => `|${prefixo}:${dims[prefixo]}`).join("");
}

function createNode(key, level, label, extra = {}) {
  return {
    key,
    level,
    label,
    producaoLiquida: 0,
    comissaoCorrespondente: 0,
    comissaoParceiro: 0,
    linhas: 0,
    dims: {},
    children: new Map(),
    ...extra,
  };
}

/**
 * As dimensões que a tabela pode agrupar, em qualquer ordem.
 *
 * A Join devolve LINHAS, com banco, convênio, parceiro e valores em cada uma — a hierarquia é
 * montada aqui. Por isso trocar a ordem (parceiro › convênio › banco, por exemplo) não custa
 * consulta nenhuma: é o mesmo dado, agrupado de outro jeito.
 */
const DIMENSOES = {
  BANCO: {
    label: "Banco",
    plural: "bancos",
    prefixo: "b",
    id: (linha) => linha?.bancoId,
    nome: (linha) => String(linha?.bancoNome || "").trim() || "Banco não informado",
  },
  CONVENIO: {
    label: "Convênio",
    plural: "convênios",
    prefixo: "c",
    id: (linha) => linha?.convenioId,
    nome: (linha) => String(linha?.convenioNome || "").trim() || "Convênio não informado",
  },
  PARCEIRO: {
    label: "Parceiro",
    plural: "parceiros",
    prefixo: "p",
    id: (linha) => linha?.parceiroIdJoin,
    nome: (linha) => String(linha?.parceiroNome || "").trim() || "Parceiro não informado",
  },
};

const ORDEM_PADRAO = ["BANCO", "CONVENIO", "PARCEIRO"];

function buildTree(linhas, ordem = ORDEM_PADRAO) {
  const dimensoes = (ordem?.length ? ordem : ORDEM_PADRAO).map((chave) => DIMENSOES[chave]).filter(Boolean);
  const root = createNode("root", -1, "Total");

  linhas.forEach((linha) => {
    const caminho = [root];
    let chaveAcumulada = "";
    let pai = root;

    dimensoes.forEach((dimensao, nivel) => {
      const nome = dimensao.nome(linha);
      const valor = dimensao.id(linha) ?? nome;
      chaveAcumulada += `|${dimensao.prefixo}:${valor}`;
      let no = pai.children.get(chaveAcumulada);
      if (!no) {
        // idJoin so no no de parceiro: e ele que abre a vida do agente, em qualquer nivel.
        no = createNode(chaveAcumulada, nivel, nome, {
          dims: { ...pai.dims, [dimensao.prefixo]: valor },
          ...(dimensao === DIMENSOES.PARCEIRO ? { idJoin: linha?.parceiroIdJoin ?? null } : {}),
        });
        pai.children.set(chaveAcumulada, no);
      }
      caminho.push(no);
      pai = no;
    });

    caminho.forEach((node) => {
      node.producaoLiquida += toNumber(linha?.producaoLiquida);
      node.comissaoCorrespondente += toNumber(linha?.comissaoCorrespondente);
      node.comissaoParceiro += toNumber(linha?.comissaoParceiro);
      node.linhas += 1;
    });
  });
  return root;
}

function sortChildren(node, sort, repasseOf) {
  const factor = sort.direction === "asc" ? 1 : -1;
  const valueOf = (child) => (sort.key === "repasse" ? repasseOf(child) : child[sort.key]);
  return Array.from(node.children.values()).sort((left, right) => {
    const byName = left.label.localeCompare(right.label, "pt-BR");
    if (sort.key === "label") return factor * byName;
    const leftValue = valueOf(left);
    const rightValue = valueOf(right);
    // Sem comissão do correspondente não há repasse: vai sempre para o fim, nos dois sentidos.
    if (leftValue === null || rightValue === null) {
      if (leftValue === rightValue) return byName;
      return leftValue === null ? 1 : -1;
    }
    if (leftValue === rightValue) return byName;
    return leftValue > rightValue ? factor : -factor;
  });
}

// Linhas visíveis na ordem da árvore: banco, e abaixo dele os convênios e parceiros abertos.
function flattenVisibleRows(root, sort, isExpanded, repasseOf) {
  const rows = [];
  const walk = (node) => {
    sortChildren(node, sort, repasseOf).forEach((child) => {
      const expandable = child.children.size > 0;
      const expanded = expandable && isExpanded(child.key);
      rows.push({ node: child, expandable, expanded });
      if (expanded) walk(child);
    });
  };
  walk(root);
  return rows;
}

/** Quantos nos existem no ultimo nivel, seja ele qual for na ordem escolhida. */
function countLeaves(root) {
  let total = 0;
  const walk = (node) => {
    if (!node.children.size) {
      total += 1;
      return;
    }
    node.children.forEach(walk);
  };
  root.children.forEach(walk);
  return total;
}

function KpiTile({ icon: Icon, label, value, helper, accent }) {
  return (
    <article
      className="relative min-w-0 overflow-hidden rounded-2xl text-kpi-foreground shadow-md transition-shadow hover:shadow-lg"
      style={kpiGradientStyle(accent)}
    >
      <div className="relative flex min-w-0 items-start justify-between gap-3 p-5">
        <div className="min-w-0 space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-kpi-foreground/90">{label}</p>
          <p className="max-w-full break-all font-display text-[26px] font-semibold leading-tight tabular-nums">{value}</p>
          {helper ? <p className="text-xs text-kpi-foreground/85">{helper}</p> : null}
        </div>
        <Icon className="h-6 w-6 shrink-0 text-kpi-foreground/90" aria-hidden="true" />
      </div>
    </article>
  );
}

function SortHeader({ label, sortKey, sort, onSort, align = "right", className }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      className={cn(
        "sticky top-0 z-20 whitespace-nowrap border-b border-border bg-card px-3 py-2 font-medium text-muted-foreground",
        align === "right" ? "text-right" : "text-left",
        className
      )}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md text-xs uppercase tracking-wide transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          align === "right" && "flex-row-reverse",
          active && "text-foreground"
        )}
      >
        <span>{label}</span>
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      </button>
    </th>
  );
}

function RepasseValue({ value, showBar = true }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  const width = Math.max(0, Math.min(100, value));
  return (
    <span className="inline-flex items-center justify-end gap-2">
      {showBar ? (
        <span className="h-1.5 w-14 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <span className="block h-full rounded-full" style={{ width: `${width}%`, backgroundColor: REPASSE_COLOR }} />
        </span>
      ) : null}
      <span>{formatPercent(value)}</span>
    </span>
  );
}

export default function ComissaoRepasse() {
  const navigate = useNavigate();
  const todayIso = getTodayIso();
  const [filters, setFilters] = useState(() => loadFilters());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [preFiltroIds, setPreFiltroIds] = useState([]);
  const [usuario, setUsuario] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [parceiros, setParceiros] = useState([]);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "repasse", direction: "desc" });
  // Ordem de agrupamento escolhida por quem usa. Fica no navegador porque e preferencia de
  // visualizacao, nao configuracao do sistema.
  const [ordemAgrupamento, setOrdemAgrupamento] = useState(() => {
    try {
      const salvo = JSON.parse(window.localStorage.getItem("repasse:ordem-agrupamento"));
      const valido = Array.isArray(salvo) && salvo.length && salvo.every((chave) => DIMENSOES[chave]);
      return valido ? salvo : ORDEM_PADRAO;
    } catch {
      return ORDEM_PADRAO;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("repasse:ordem-agrupamento", JSON.stringify(ordemAgrupamento));
    } catch {
      // Navegador sem armazenamento (aba anonima, cookies bloqueados): a ordem vale so nesta sessao.
    }
  }, [ordemAgrupamento]);

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const requestSequenceRef = useRef(0);
  const isAdmin = isAdminUser(usuario);
  // Valor de comissao e numero de diretoria: o backend nao manda para quem nao e admin
  // (DashboardComercialService.consultarComissaoRepasse), e aqui as colunas somem junto para a tela
  // nao ficar com coluna vazia. O repasse (%) todos veem: para quem nao e admin ele vem pronto do
  // servidor, em `repasses`.
  const mostrarComissao = isAdmin;
  const colunasVisiveis = useMemo(
    () =>
      mostrarComissao
        ? VALUE_COLUMNS
        : VALUE_COLUMNS.filter((coluna) => coluna.key === "producaoLiquida" || coluna.key === "repasse"),
    [mostrarComissao]
  );

  // Defesa: se a coluna ordenada nao estiver visivel, a tabela ficaria ordenada por algo que nao
  // esta na tela e o rodape diria "ordenado por" uma coluna que sumiu.
  useEffect(() => {
    if (mostrarComissao) return;
    setSort((atual) => (colunasVisiveis.some((coluna) => coluna.key === atual.key) || atual.key === "label"
      ? atual
      : { key: "producaoLiquida", direction: "desc" }));
  }, [mostrarComissao, colunasVisiveis]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // Sessão indisponível: os filtros só não sobrevivem ao recarregar.
    }
  }, [filters]);

  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      try {
        const user = await getUsuarioLogado();
        const admin = isAdminUser(user);
        // Listas de apoio (opções de filtro e vínculo do parceiro): se alguma falhar, o relatório
        // continua funcionando sem ela.
        const [bancosResult, conveniosResult, parceirosResult, usuariosResult] = await Promise.allSettled([
          listarBancosAtivos(),
          listarConveniosAtivos(),
          listarParceiros(),
          admin ? listarTodosUsuariosAtivos() : Promise.resolve([]),
        ]);
        if (!mounted) return;
        const valueOf = (result) => (result.status === "fulfilled" && Array.isArray(result.value) ? result.value : []);
        setUsuario(user);
        setBancos(valueOf(bancosResult));
        setConvenios(valueOf(conveniosResult));
        setParceiros(valueOf(parceirosResult));
        setUsuarios(valueOf(usuariosResult));
      } finally {
        if (mounted) setBootstrapping(false);
      }
    }
    bootstrap();
    return () => {
      mounted = false;
    };
  }, []);

  const bancoOptions = useMemo(
    () => bancos.map((item) => ({ value: String(item.bancoId), label: item.bancoNome })),
    [bancos]
  );
  const convenioOptions = useMemo(
    () => convenios.map((item) => ({ value: String(item.convenioId), label: item.convenioNome })),
    [convenios]
  );
  const tipoProdutoOptions = useMemo(() => {
    const map = new Map();
    convenios.forEach((convenio) => {
      (convenio?.tiposConvenio || []).forEach((tipo) => {
        const codigo = String(tipo?.codigo || "").trim().toUpperCase();
        if (!codigo || map.has(codigo)) return;
        map.set(codigo, {
          value: codigo,
          label: String(tipo?.nome || codigo).trim() || codigo,
          ordem: Number.isFinite(Number(tipo?.ordem)) ? Number(tipo.ordem) : TYPE_ORDER[codigo] ?? 999,
        });
      });
    });
    const options = Array.from(map.values());
    const source = options.length
      ? options
      : [
          { value: "NOVO", label: "Contrato Novo", ordem: 0 },
          { value: "PORT", label: "Portabilidade", ordem: 1 },
          { value: "REFIN", label: "Refinanciamento", ordem: 2 },
        ];
    return source
      .sort((left, right) => left.ordem - right.ordem || left.label.localeCompare(right.label))
      .map(({ ordem, ...option }) => option);
  }, [convenios]);
  const supervisorOptions = useMemo(() => {
    const map = new Map();
    usuarios.forEach((user) => {
      String(user?.supervisorIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .forEach((id) => map.set(id, { value: id, label: user.nome }));
    });
    return Array.from(map.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [usuarios]);
  const parceiroByJoinId = useMemo(() => {
    const map = new Map();
    parceiros.forEach((parceiro) => {
      const idJoin = Number(parceiro?.idJoin);
      if (Number.isFinite(idJoin) && idJoin > 0) map.set(idJoin, parceiro);
    });
    return map;
  }, [parceiros]);

  const requestBase = useMemo(
    () => ({
      supervisorId: isAdmin ? filters.supervisorIds.map(Number).filter((value) => Number.isFinite(value) && value > 0) : [],
      dataInicio: filters.dataInicio,
      dataFim: filters.dataFim,
      bancosIds: filters.bancosIds.map(Number).filter(Boolean),
      conveniosIds: filters.conveniosIds.map(Number).filter(Boolean),
      tiposConvenio: filters.tiposConvenio,
      tipoPeriodo: filters.tipoPeriodo,
    }),
    [filters, isAdmin]
  );

  useEffect(() => {
    if (bootstrapping || !requestBase.dataInicio || !requestBase.dataFim) return;
    const sequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = sequence;
    setLoading(true);
    setError("");
    buscarComissaoRepasse(requestBase)
      .then((data) => {
        if (requestSequenceRef.current === sequence) setReport(data);
      })
      .catch((requestError) => {
        if (requestSequenceRef.current !== sequence) return;
        setError(requestError?.response?.data?.message || "Não foi possível consultar comissões e repasse.");
      })
      .finally(() => {
        if (requestSequenceRef.current === sequence) setLoading(false);
      });
  }, [bootstrapping, requestBase]);

  const linhas = useMemo(() => (Array.isArray(report?.linhas) ? report.linhas : []), [report]);
  const totalTree = useMemo(() => buildTree(linhas, ordemAgrupamento), [linhas, ordemAgrupamento]);
  // Repasse de um no: calculado aqui quando a linha traz as comissoes (admin); senao, o que o servidor
  // calculou para o grupo. O do servidor vale para o grupo inteiro, entao com busca so serve se o no
  // ainda tem todas as linhas do grupo; um banco com so parte dos parceiros mostra "—".
  const repasseOf = useMemo(() => {
    const repasses = report?.repasses || {};
    const linhasPorChave = new Map();
    const walk = (node) => {
      linhasPorChave.set(node.key, node.linhas);
      node.children.forEach(walk);
    };
    walk(totalTree);
    return (node) => {
      const calculado = computeRepasse(node);
      if (calculado !== null) return calculado;
      if (linhasPorChave.get(node.key) !== node.linhas) return null;
      const doServidor = repasses[repasseKey(node.dims)];
      return doServidor === undefined || doServidor === null ? null : toNumber(doServidor);
    };
  }, [report, totalTree]);
  const normalizedSearch = normalizeSearchText(search);
  const visibleTree = useMemo(() => {
    if (!normalizedSearch) return totalTree;
    return buildTree(
      linhas.filter((linha) =>
        normalizeSearchText(`${linha?.bancoNome} ${linha?.convenioNome} ${linha?.parceiroNome}`).includes(normalizedSearch)
      ),
      ordemAgrupamento
    );
  }, [linhas, totalTree, normalizedSearch, ordemAgrupamento]);
  // Com busca, tudo aparece aberto: o que casou pode estar dentro de um banco recolhido.
  const visibleRows = useMemo(
    () => flattenVisibleRows(visibleTree, sort, (key) => Boolean(normalizedSearch) || expandedKeys.has(key), repasseOf),
    [visibleTree, sort, normalizedSearch, expandedKeys, repasseOf]
  );
  // Todo no que tem filhos pode ser expandido, em qualquer profundidade: a ordem de agrupamento e
  // escolhida por quem usa, entao nao da para assumir "banco e convenio".
  const groupKeys = useMemo(() => {
    const keys = [];
    const walk = (node) => {
      if (!node.children.size) return;
      keys.push(node.key);
      node.children.forEach(walk);
    };
    totalTree.children.forEach(walk);
    return keys;
  }, [totalTree]);
  const allExpanded = groupKeys.length > 0 && groupKeys.every((key) => expandedKeys.has(key));

  const toggleNode = (key) => {
    setExpandedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSort = (key) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "label" ? "asc" : "desc" }
    );
  };

  const defaults = getDefaultFilters();
  const filtersAreDefault =
    filters.dataInicio === defaults.dataInicio &&
    filters.dataFim === defaults.dataFim &&
    !filters.bancosIds.length &&
    !filters.conveniosIds.length &&
    !filters.tiposConvenio.length &&
    !filters.supervisorIds.length &&
    filters.tipoPeriodo === defaults.tipoPeriodo;
  // Mesma contagem do dashboard: período e base sempre contam, mais cada lista preenchida.
  const activeFilterCount =
    2 +
    [filters.bancosIds, filters.conveniosIds, filters.tiposConvenio, isAdmin ? filters.supervisorIds : []].filter(
      (values) => values.length
    ).length;
  usePageFilters({ count: activeFilterCount, onOpen: () => setFiltersOpen(true) });

  if (bootstrapping) {
    return <PageLoader label="Carregando comissões..." />;
  }

  const tipoPeriodoLabel = TIPO_PERIODO_OPTIONS.find((option) => option.value === filters.tipoPeriodo)?.label || "Aprovação";
  const totalRepasse = repasseOf(totalTree);
  const joinIndisponivel = report?.escopo?.joinDisponivel === false;
  const cellClassName = (level) =>
    cn(
      "whitespace-nowrap border-b border-border px-3 py-2 text-right tabular-nums text-foreground transition-colors group-hover:bg-accent",
      level === 0 && "font-semibold"
    );
  const footerCellClassName =
    "sticky bottom-0 z-20 whitespace-nowrap border-t border-border bg-muted px-3 py-3 text-right text-sm font-bold tabular-nums text-foreground";

  let tableContent;
  if (!report && loading) {
    tableContent = (
      <div className="space-y-2" aria-hidden="true">
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="h-9 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  } else if (!visibleRows.length) {
    tableContent = (
      <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {error ||
          (joinIndisponivel
            ? "Seu usuário não tem Supervisor ID configurado. Sem ele não é possível consultar a Join."
            : normalizedSearch && linhas.length
              ? `Nenhum banco, convênio ou parceiro encontrado para "${search.trim()}".`
              : "Nenhuma produção com comissão no período e filtros selecionados.")}
      </div>
    );
  } else {
    tableContent = (
      <div className="max-h-[640px] overflow-auto rounded-lg border border-border">
        <table className="w-full min-w-[760px] border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <SortHeader
                label="Banco › Convênio › Parceiro"
                sortKey="label"
                sort={sort}
                onSort={handleSort}
                align="left"
                className="left-0 z-30 min-w-[320px]"
              />
              {colunasVisiveis.map((column) => (
                <SortHeader key={column.key} label={column.label} sortKey={column.key} sort={sort} onSort={handleSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(({ node, expandable, expanded }) => {
              const partner = node.idJoin ? parceiroByJoinId.get(Number(node.idJoin)) : null;
              return (
                <tr key={node.key} className="group">
                  <td className="sticky left-0 z-10 max-w-[380px] border-b border-border bg-card px-3 py-2 transition-colors group-hover:bg-accent">
                    <div className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: `${node.level * 20}px` }}>
                      {expandable ? (
                        <button
                          type="button"
                          onClick={() => toggleNode(node.key)}
                          disabled={Boolean(normalizedSearch)}
                          aria-expanded={expanded}
                          aria-label={`${expanded ? "Recolher" : "Expandir"} ${node.label}`}
                          className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                        >
                          <ChevronRight className={cn("h-4 w-4 transition-transform", expanded && "rotate-90")} />
                        </button>
                      ) : (
                        <span className="w-6 shrink-0" aria-hidden="true" />
                      )}
                      {partner ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/parceiros/${partner.id}/vida-agente`)}
                          className="truncate rounded-sm text-left text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          title={`Abrir vida do agente de ${node.label}`}
                        >
                          {node.label}
                        </button>
                      ) : (
                        <span
                          className={cn(
                            "truncate text-foreground",
                            node.level === 0 && "font-semibold",
                            node.level === 1 && "font-medium"
                          )}
                          title={node.label}
                        >
                          {node.label}
                        </span>
                      )}
                      {expandable ? (
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">({node.children.size})</span>
                      ) : null}
                    </div>
                  </td>
                  <td className={cellClassName(node.level)}>{formatCurrency(node.producaoLiquida)}</td>
                  {mostrarComissao ? (
                    <>
                      <td className={cellClassName(node.level)}>{formatCurrency(node.comissaoCorrespondente)}</td>
                      <td className={cellClassName(node.level)}>{formatCurrency(node.comissaoParceiro)}</td>
                    </>
                  ) : null}
                  <td className={cellClassName(node.level)}>
                    <RepasseValue value={repasseOf(node)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className={cn(footerCellClassName, "left-0 z-30 text-left")}>
                <span className="block text-xs font-bold uppercase tracking-[0.16em]">
                  {normalizedSearch ? "Total da busca" : "Total geral"}
                </span>
                <span className="block text-[11px] font-medium normal-case tracking-normal text-muted-foreground">
                  {visibleTree.children.size.toLocaleString("pt-BR")} {DIMENSOES[ordemAgrupamento[0]].plural} ·{" "}
                  {countLeaves(visibleTree).toLocaleString("pt-BR")}{" "}
                  {DIMENSOES[ordemAgrupamento[ordemAgrupamento.length - 1]].plural}
                </span>
              </td>
              <td className={footerCellClassName}>{formatCurrency(visibleTree.producaoLiquida)}</td>
              {mostrarComissao ? (
                <>
                  <td className={footerCellClassName}>{formatCurrency(visibleTree.comissaoCorrespondente)}</td>
                  <td className={footerCellClassName}>{formatCurrency(visibleTree.comissaoParceiro)}</td>
                </>
              ) : null}
              <td className={cn(footerCellClassName, "text-base")}>
                <RepasseValue value={repasseOf(visibleTree)} showBar={false} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  const expandToggle = (
    <Button
      key="expandir"
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setExpandedKeys(allExpanded ? new Set() : new Set(groupKeys))}
      disabled={!groupKeys.length || Boolean(normalizedSearch)}
    >
      {allExpanded ? <ChevronsDownUp className="mr-2 h-4 w-4" /> : <ChevronsUpDown className="mr-2 h-4 w-4" />}
      {allExpanded ? "Recolher tudo" : "Expandir tudo"}
    </Button>
  );
  const searchField = (
    <SearchBar
      key="busca"
      placeholder="Buscar banco, convênio ou parceiro"
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      className="w-full sm:w-72"
    />
  );

  return (
    <div className="min-w-0 space-y-5">
      <FiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Aplicados à análise de comissões."
        onClear={() => setFilters(getDefaultFilters())}
        clearDisabled={filtersAreDefault}
      >
        <div className="grid gap-5">
          <PreFiltroField
            value={preFiltroIds}
            onChange={setPreFiltroIds}
            onApply={(vetores) => setFilters((prev) => ({ ...prev, ...vetores }))}
          />
          <DateRangeField
            label="Período analisado"
            from={filters.dataInicio}
            to={filters.dataFim}
            maxDate={todayIso}
            onFromChange={(event) => setFilters((prev) => ({ ...prev, dataInicio: event.target.value }))}
            onToChange={(event) => setFilters((prev) => ({ ...prev, dataFim: event.target.value }))}
          />
          <MultiSelectField
            label="Bancos"
            placeholder="Todos os bancos"
            options={bancoOptions}
            selectedValues={filters.bancosIds}
            onChange={(values) => setFilters((prev) => ({ ...prev, bancosIds: values }))}
          />
          <MultiSelectField
            label="Convênios"
            placeholder="Todos os convênios"
            options={convenioOptions}
            selectedValues={filters.conveniosIds}
            onChange={(values) => setFilters((prev) => ({ ...prev, conveniosIds: values }))}
          />
          <MultiSelectField
            label="Tipo de produto"
            placeholder="Todos os tipos de produto"
            options={tipoProdutoOptions}
            selectedValues={filters.tiposConvenio}
            onChange={(values) => setFilters((prev) => ({ ...prev, tiposConvenio: values }))}
          />
          {isAdmin ? (
            <MultiSelectField
              label="Supervisor"
              placeholder="Todos os supervisores"
              options={supervisorOptions}
              selectedValues={filters.supervisorIds}
              onChange={(values) => setFilters((prev) => ({ ...prev, supervisorIds: values }))}
            />
          ) : null}
          <div className="min-w-0 space-y-1.5">
            <label className="block text-sm font-medium text-foreground">Período base</label>
            <div className="grid w-full grid-cols-2 gap-1 rounded-lg border border-input bg-background p-1 shadow-sm">
              {TIPO_PERIODO_OPTIONS.map((option) => {
                const active = filters.tipoPeriodo === option.value;
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilters((prev) => ({ ...prev, tipoPeriodo: option.value }))}
                    className={
                      active
                        ? "flex h-10 items-center justify-center gap-2 rounded-md border border-transparent bg-action px-3 text-action-foreground shadow-sm transition"
                        : "flex h-10 items-center justify-center gap-2 rounded-md border border-transparent px-3 text-muted-foreground transition hover:border-border/70 hover:bg-background/80 hover:text-foreground"
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span className="truncate text-sm font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </FiltersSheet>

      {/* Mesma barra do dashboard: Comissões é a quarta aba, só que numa rota própria. */}
      <DashboardTabs value="comissoes" />

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <KpiTile icon={Wallet} label="Produção líquida" value={formatCurrency(totalTree.producaoLiquida, 0)} helper="No período" accent="var(--chart-2)" />
        {mostrarComissao ? (
          <>
            <KpiTile
              icon={CircleDollarSign}
              label="Comissão correspondente"
              value={formatCurrency(totalTree.comissaoCorrespondente, 0)}
              helper="Prevista na capa do contrato"
              accent="var(--chart-1)"
            />
            <KpiTile
              icon={HandCoins}
              label="Comissão parceiro"
              value={formatCurrency(totalTree.comissaoParceiro, 0)}
              helper="Prevista na capa do contrato"
              accent="var(--chart-3)"
            />
          </>
        ) : null}
        <KpiTile
          icon={Percent}
          label="Repasse parceiro"
          value={formatPercent(totalRepasse)}
          helper="Comissão parceiro ÷ correspondente"
          accent={REPASSE_COLOR}
        />
      </div>

      <Card title={ordemAgrupamento.map((chave) => DIMENSOES[chave].label).join(" › ")} actions={[searchField, expandToggle]}>
        <div className="space-y-3">
          {/* Arrastar reordena, clicar oculta. Ver components/OrdemAgrupamento.jsx. */}
          <OrdemAgrupamento
            dimensoes={DIMENSOES}
            ordem={ordemAgrupamento}
            onChange={setOrdemAgrupamento}
            padrao={ORDEM_PADRAO}
          />

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <p>
              {formatIsoToBr(filters.dataInicio)} – {formatIsoToBr(filters.dataFim)} · base {tipoPeriodoLabel.toLowerCase()} ·
              ordenado por {sort.key === "label" ? "nome" : colunasVisiveis.find((column) => column.key === sort.key)?.label.toLowerCase()}
            </p>
            {loading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-label="Atualizando" /> : null}
          </div>
          {error && visibleRows.length ? <p className="text-sm text-destructive">{error}</p> : null}
          {tableContent}
        </div>
      </Card>
    </div>
  );
}
